import React from "react";
import { LuTrendingUp, LuBarChart3, LuCalendar } from "react-icons/lu";

interface TrendChartLoadingProps {
  chartType: "line" | "area" | "bar";
  timeGranularity: string;
  metric: string;
}

export const TrendChartLoading: React.FC<TrendChartLoadingProps> = ({ 
  chartType, 
  timeGranularity, 
  metric 
}) => {
  const renderSkeletonBars = () => {
    const bars = [];
    const barCount = timeGranularity === "daily" ? 30 : 
                    timeGranularity === "weekly" ? 12 : 
                    timeGranularity === "monthly" ? 12 : 5;

    for (let i = 0; i < barCount; i++) {
      const height = Math.random() * 200 + 100; // Random height between 100-300
      bars.push(
        <div
          key={i}
          className="flex-1 bg-gray-200 rounded animate-pulse"
          style={{ height: `${height}px` }}
        />
      );
    }
    return bars;
  };

  const renderSkeletonLines = () => {
    const lines = [];
    const pointCount = timeGranularity === "daily" ? 30 : 
                      timeGranularity === "weekly" ? 12 : 
                      timeGranularity === "monthly" ? 12 : 5;

    for (let i = 0; i < pointCount; i++) {
      lines.push(
        <div
          key={i}
          className="w-3 h-3 bg-gray-300 rounded-full animate-pulse"
        />
      );
    }
    return lines;
  };

  const renderSkeletonAreas = () => {
    const areas = [];
    const pointCount = timeGranularity === "daily" ? 30 : 
                      timeGranularity === "weekly" ? 12 : 
                      timeGranularity === "monthly" ? 12 : 5;

    for (let i = 0; i < pointCount; i++) {
      areas.push(
        <div
          key={i}
          className="flex-1 bg-gradient-to-t from-gray-200 to-gray-100 rounded animate-pulse"
          style={{ height: `${Math.random() * 150 + 100}px` }}
        />
      );
    }
    return areas;
  };

  return (
    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
      {/* Header Skeleton */}
      <div className="mb-4">
        <div className="h-6 bg-gray-200 rounded w-48 mb-2 animate-pulse"></div>
        <div className="h-4 bg-gray-200 rounded w-64 animate-pulse"></div>
      </div>

      {/* Chart Container Skeleton */}
      <div className="relative">
        {/* Chart Type Specific Skeleton */}
        <div className="h-96 flex items-end justify-between gap-2">
          {chartType === "bar" && renderSkeletonBars()}
          {chartType === "line" && (
            <div className="w-full h-full flex items-center justify-center">
              <div className="flex items-center gap-2">
                {renderSkeletonLines()}
              </div>
            </div>
          )}
          {chartType === "area" && (
            <div className="w-full h-full flex items-end gap-1">
              {renderSkeletonAreas()}
            </div>
          )}
        </div>

        {/* X-Axis Skeleton */}
        <div className="mt-4 flex justify-between">
          {timeGranularity === "daily" && Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-3 bg-gray-200 rounded w-8 animate-pulse"></div>
          ))}
          {timeGranularity === "weekly" && Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-3 bg-gray-200 rounded w-12 animate-pulse"></div>
          ))}
          {timeGranularity === "monthly" && Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-3 bg-gray-200 rounded w-16 animate-pulse"></div>
          ))}
          {timeGranularity === "yearly" && Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-3 bg-gray-200 rounded w-12 animate-pulse"></div>
          ))}
        </div>

        {/* Y-Axis Skeleton */}
        <div className="absolute left-0 top-0 h-full flex flex-col justify-between">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-3 bg-gray-200 rounded w-12 animate-pulse"></div>
          ))}
        </div>

        {/* Loading Overlay */}
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 text-gray-600 mb-2">
              <LuTrendingUp className="animate-bounce" />
              <span className="text-sm font-medium">Loading {metric} trends...</span>
            </div>
            <div className="text-xs text-gray-500">
              {chartType === "line" && <LuTrendingUp className="inline mr-1" />}
              {chartType === "bar" && <LuBarChart3 className="inline mr-1" />}
              {chartType === "area" && <LuCalendar className="inline mr-1" />}
              {chartType} chart • {timeGranularity} data
            </div>
          </div>
        </div>
      </div>

      {/* Legend Skeleton */}
      <div className="mt-4 flex gap-4 justify-center">
        {["Learners", "Coaches", "Dual Role", "Total"].map((label, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="w-3 h-3 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-3 bg-gray-200 rounded w-16 animate-pulse"></div>
          </div>
        ))}
      </div>
    </div>
  );
};
